// src/pages/SettingsPage.tsx
import React, { useState, useEffect } from 'react';
import {
    CLIENT_ID_STORAGE_KEY,
    AUTH_STATE_KEY,
    CODE_VERIFIER_KEY,
    generateState,
    generatePkce,
    getAuthorizationUrl,
    isAuthenticated,
    clearAuthData,
    REDIRECT_URI,
} from '../utils/redditAuth';
import { getMe } from '../api/redditApi';
import {
    getRewriteXLinksSetting,
    setRewriteXLinksSetting,
    getShowSelftextPreviewSetting,
    setShowSelftextPreviewSetting,
    getGeminiApiKeySetting,     // Import Gemini getter
    setGeminiApiKeySetting,     // Import Gemini setter
} from '../utils/settings';
import { clearCache } from '../api/cache';

const SettingsPage: React.FC = () => {
    const [clientId, setClientId] = useState<string>('');
    const [geminiApiKey, setGeminiApiKey] = useState<string>(''); // State for Gemini Key
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [isAuth, setIsAuth] = useState<boolean>(false);
    const [userInfo, setUserInfo] = useState<any>(null);
    const [rewriteXLinks, setRewriteXLinks] = useState<boolean>(false);
    const [showSelftextPreview, setShowSelftextPreview] = useState<boolean>(true);
    const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);

    // Check auth status, load Client ID, fetch user info, and load settings on mount
    useEffect(() => {
        const checkAuthAndFetchUser = async () => {
            const authenticated = isAuthenticated();
            setIsAuth(authenticated);
            if (authenticated) {
                try {
                    const data = await getMe();
                    setUserInfo(data);
                    setIsAuth(true);
                } catch (error: any) {
                    console.error("Failed to fetch user info (token might be invalid/revoked):", error);
                    handleLogout(false);
                    setUserInfo(null);
                    setIsAuth(false);
                    showMessage('Session expired or invalid. Please log in again.', 'error');
                }
            } else {
                setUserInfo(null);
                setIsAuth(false);
            }
        };

        checkAuthAndFetchUser();
        const savedClientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
        if (savedClientId) setClientId(savedClientId);
        const savedGeminiKey = getGeminiApiKeySetting(); // Load Gemini key
        if (savedGeminiKey) setGeminiApiKey(savedGeminiKey);
        setRewriteXLinks(getRewriteXLinksSetting());
        setShowSelftextPreview(getShowSelftextPreviewSetting());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showMessage = (text: string, type: 'success' | 'error' | 'info', duration = 3000) => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), duration);
    };

    // Input change handlers
    const handleClientIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setClientId(event.target.value);
        setMessage(null);
    };

    const handleGeminiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGeminiApiKey(event.target.value);
        setMessage(null);
    };

    // Save handlers
    const handleSaveClientId = () => {
        const trimmedId = clientId.trim();
        if (trimmedId) {
            localStorage.setItem(CLIENT_ID_STORAGE_KEY, trimmedId);
            showMessage('Client ID saved.', 'success');
        } else {
            showMessage('Please enter a valid Client ID.', 'error');
        }
    };

    const handleSaveGeminiKey = () => {
        const trimmedKey = geminiApiKey.trim();
        if (trimmedKey) {
            setGeminiApiKeySetting(trimmedKey); // Use setter function
            showMessage('Gemini API Key saved.', 'success');
        } else {
            showMessage('Please enter a valid Gemini API Key.', 'error');
        }
    };

    // Clear handlers
    const handleClearClientId = () => {
        localStorage.removeItem(CLIENT_ID_STORAGE_KEY);
        setClientId('');
        if (isAuth) {
            handleLogout(false);
        }
        showMessage('Client ID cleared.', 'info');
    }

    const handleClearGeminiKey = () => {
        setGeminiApiKeySetting(null); // Use setter function with null
        setGeminiApiKey('');
        showMessage('Gemini API Key cleared.', 'info');
    }

    // Login handler
    const handleLogin = async () => {
        setIsLoginLoading(true);
        setMessage(null);
        const currentClientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
        if (!currentClientId) {
            showMessage('Please save a Client ID before logging in.', 'error');
            setIsLoginLoading(false);
            return;
        }
        try {
            const state = generateState();
            const { code_verifier, code_challenge } = await generatePkce();
            localStorage.setItem(AUTH_STATE_KEY, state);
            localStorage.setItem(CODE_VERIFIER_KEY, code_verifier);
            const authUrl = getAuthorizationUrl(currentClientId, state, code_challenge);
            window.location.href = authUrl;
        } catch (error) {
            console.error("Error preparing for login:", error);
            showMessage('Could not initiate login. Check console for details.', 'error');
            setIsLoginLoading(false);
        }
    };

    // Logout handler
    const handleLogout = (showMsg = true) => {
        clearAuthData();
        setIsAuth(false);
        setUserInfo(null);
        if (showMsg) {
            showMessage('Logged out successfully.', 'info');
        }
    }

    // Preference Toggles
    const handleRewriteToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setRewriteXLinks(isChecked);
        setRewriteXLinksSetting(isChecked);
        showMessage(`X.com link rewriting ${isChecked ? 'enabled' : 'disabled'}.`, 'info', 2000);
    };

    const handleShowSelftextPreviewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setShowSelftextPreview(isChecked);
        setShowSelftextPreviewSetting(isChecked);
        showMessage(`Selftext preview on cards ${isChecked ? 'enabled' : 'disabled'}.`, 'info', 2000);
    };

    // Clear Cache
    const handleClearCache = () => {
        clearCache();
        showMessage('Application cache cleared.', 'success');
    };

    // --- Render Logic ---
    return (
        <div className="prose-wrapper">
            <div className="p-6 md:p-8 bg-white dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700/50 space-y-8">
                <header>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Settings</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Manage API connections and app preferences.</p>
                </header>

                {/* API Keys Section */}
                <section className="space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">API Keys</h2>

                    {/* Reddit Client ID */}
                    <div className="space-y-2">
                        <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Reddit Client ID
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Create an 'installed app' or 'web app' on <a href="https://www.reddit.com/prefs/apps" target="_blank" rel="noopener noreferrer">Reddit Apps</a>. Set Redirect URI to: <code className="text-xs bg-gray-100 dark:bg-gray-700 p-0.5 rounded font-mono select-all">{REDIRECT_URI}</code>
                        </p>
                        <input
                            type="text"
                            id="clientId"
                            value={clientId}
                            onChange={handleClientIdChange}
                            placeholder="Enter Reddit Client ID"
                            className="block w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 disabled:bg-gray-100 dark:disabled:bg-gray-700/50 disabled:cursor-not-allowed"
                            disabled={isAuth}
                            aria-describedby="clientId-description"
                        />
                        <p id="clientId-description" className="text-xs text-gray-500 dark:text-gray-400">
                            {isAuth ? "Log out to change the Client ID." : "Stored locally."}
                        </p>
                        <div className="flex flex-wrap gap-3 pt-1">
                            <button onClick={handleSaveClientId} disabled={isAuth} className="px-3 py-1 text-sm font-medium rounded border border-indigo-600 text-indigo-700 dark:border-indigo-500 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                Save ID
                            </button>
                            <button onClick={handleClearClientId} disabled={isAuth} className="px-3 py-1 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                Clear ID
                            </button>
                        </div>
                    </div>

                    {/* Gemini API Key - NEW SECTION */}
                    <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700/50">
                        <label htmlFor="geminiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Google Gemini API Key (Optional)
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Used for generating TL;DR summaries. Get a key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">Google AI Studio</a>.
                        </p>
                        <input
                            type="password" // Use password type for keys
                            id="geminiKey"
                            value={geminiApiKey}
                            onChange={handleGeminiKeyChange}
                            placeholder="Enter Gemini API Key"
                            className="block w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        />
                         <p className="text-xs text-gray-500 dark:text-gray-400">
                            Stored locally in your browser. Required for TL;DR feature.
                        </p>
                        <div className="flex flex-wrap gap-3 pt-1">
                            <button onClick={handleSaveGeminiKey} className="px-3 py-1 text-sm font-medium rounded border border-indigo-600 text-indigo-700 dark:border-indigo-500 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500">
                                Save Key
                            </button>
                            <button onClick={handleClearGeminiKey} className="px-3 py-1 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500">
                                Clear Key
                            </button>
                        </div>
                    </div>
                </section>

                {/* Authentication Section */}
                <section className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Reddit Authentication</h2>
                    {isAuth ? (
                        <div className='space-y-3'>
                            <p className="text-sm"><span className="font-medium text-green-600 dark:text-green-400">Status:</span> Authenticated</p>
                            {userInfo && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">User:</span> {userInfo.name}
                                </p>
                            )}
                            {!userInfo && isAuth && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 italic">Fetching user info...</p>
                            )}
                            <button onClick={() => handleLogout()} className="px-3 py-1 text-sm font-medium rounded border border-red-600 text-red-700 dark:border-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus-visible:ring-1 focus-visible:ring-red-500">
                                Logout
                            </button>
                        </div>
                    ) : (
                        <div className='space-y-3'>
                            <p className="text-sm"><span className="font-medium text-red-600 dark:text-red-400">Status:</span> Not Authenticated</p>
                            <button
                                onClick={handleLogin}
                                disabled={!clientId.trim() || isLoginLoading}
                                className="px-3 py-1 text-sm font-medium rounded border border-orange-600 text-orange-700 dark:border-orange-500 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 focus:outline-none focus-visible:ring-1 focus-visible:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isLoginLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <span>Login with Reddit</span>
                                )}
                            </button>
                            {!clientId.trim() && <p className="text-xs text-gray-500 dark:text-gray-400">Save Client ID first to enable login.</p>}
                        </div>
                    )}
                </section>

                {/* Preferences Section */}
                <section className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Preferences</h2>
                    {/* Rewrite Links Toggle */}
                    <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                            <input
                                id="rewriteXLinks"
                                name="rewriteXLinks"
                                type="checkbox"
                                checked={rewriteXLinks}
                                onChange={handleRewriteToggle}
                                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-500 focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 dark:bg-gray-700 cursor-pointer"
                            />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                            <label htmlFor="rewriteXLinks" className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                                Rewrite x.com Links
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Automatically change links from <code className="text-xs bg-gray-100 dark:bg-gray-700 p-0.5 rounded font-mono">x.com</code> to <code className="text-xs bg-gray-100 dark:bg-gray-700 p-0.5 rounded font-mono">xcancel.com</code>.
                            </p>
                        </div>
                    </div>
                    {/* Show Selftext Preview Toggle */}
                    <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                            <input
                                id="show-selftext-preview"
                                aria-describedby="show-selftext-preview-description"
                                name="show-selftext-preview"
                                type="checkbox"
                                checked={showSelftextPreview}
                                onChange={handleShowSelftextPreviewChange}
                                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                            />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                            <label htmlFor="show-selftext-preview" className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                                Show selftext preview on cards
                            </label>
                            <p id="show-selftext-preview-description" className="text-gray-500 dark:text-gray-400 text-xs">
                                Display a few lines of the post's text content directly on the post card in list views.
                            </p>
                        </div>
                    </div>
                    {/* Clear Cache Button */}
                    <div>
                        <button
                            onClick={handleClearCache}
                            className="px-3 py-1 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500"
                        >
                            Clear Cached API Data
                        </button>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Clears locally cached data from Reddit API requests. Useful if content seems stale.
                        </p>
                    </div>
                </section>

                {/* Feedback Message Area */}
                {message && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                        <p className={`text-sm ${
                            message.type === 'success' ? 'text-green-700 dark:text-green-400'
                            : message.type === 'error' ? 'text-red-700 dark:text-red-400'
                            : 'text-blue-700 dark:text-blue-400'
                        } transition-opacity duration-300 ease-in-out`}>
                            {message.text}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;